import React from 'react';
import classnames from 'classnames';
import './VenueAnalysisGrid.scss';
import {
    VenueAnalysisData,
    VenueAnalysisVenueTypeData,
} from '../venueAnalysisAggregatedData';
import {
    computeBarWidths,
    VenueAnalysisVenueDisplayData,
    getSortedVenueTypes,
    filterByVenueType,
} from './gridData';
import { VenueAnalysisBar } from './VenueAnalysisBar';
import { VenueAnalysisToggleLabel } from './VenueAnalysisToggleLabel';
import { VenueAnalysisConfigurationState } from '../store/configuration/VenueAnalysisConfigurationState';
import ShowHideToggle from 'utils/components/ShowHideToggle/ShowHideToggle';
import {
    VenueAnalysisVisibleBreakdowns,
    toggleVenueAnalysisComponentProperty,
} from '../store/component/VenueAnalysisComponentState';
import {
    historicalTooltip,
    todayTooltip,
} from 'widgets/venueAnalysis/help/venueAnalysisTooltips';
import { VenueAnalysisSelection } from 'widgets/venueAnalysis/store/component/VenueAnalysisSelection';
import { HighlightedVenue } from 'widgets/venueAnalysis/highlighting';
import { useVenueAnalysisComponentSlice } from 'widgets/venueAnalysis/store/component/componentSlice';

interface MarketVolBreakdownProps {
    data: VenueAnalysisData;
    config: VenueAnalysisConfigurationState;
    onToggle: () => void;
    highlightedVenue?: HighlightedVenue;
    onHighlightedVenueChange: (venue?: HighlightedVenue) => void;
    selection?: VenueAnalysisSelection;
    onChangeSelection: (value?: VenueAnalysisSelection) => void;
    widgetKey: string;
    width: number;
}

export function MarketVolBreakdown({
    data,
    config,
    onToggle,
    highlightedVenue,
    onHighlightedVenueChange,
    selection,
    onChangeSelection,
    width,
}: MarketVolBreakdownProps) {
    const isVisible = config.visibleSections.market;

    const histVenueTypes = data?.mktVolHist?.venueTypes;
    const todayVenueTypes = data?.mktVolRealTime?.venueTypes;

    const { component, dispatchComponentChanged: updateComponent } =
        useVenueAnalysisComponentSlice();

    const showHistVenues = data && component.visibleBreakdowns.marketHistorical;
    const showTodayVenues = data && component.visibleBreakdowns.marketToday;

    const toggleBreakdown = (breakdown: keyof VenueAnalysisVisibleBreakdowns) =>
        updateComponent(
            toggleVenueAnalysisComponentProperty(
                component,
                'visibleBreakdowns',
                breakdown
            )
        );

    let histDisplayData: VenueAnalysisVenueDisplayData[] = histVenueTypes
        ? computeBarWidths(getSortedVenueTypes(histVenueTypes))
        : [];

    let todayDisplayData: VenueAnalysisVenueDisplayData[] = todayVenueTypes
        ? computeBarWidths(getSortedVenueTypes(todayVenueTypes))
        : [];

    function getVenuesForType(
        data: VenueAnalysisVenueTypeData[] | undefined,
        venueTypeName: string
    ): VenueAnalysisVenueDisplayData[] {
        const venues = data
            ? data.find((type) => type.name === venueTypeName)?.venues
            : [];
        return venues ? computeBarWidths(venues) : [];
    }

    if (selection) {
        histDisplayData = showHistVenues
            ? filterByVenueType(histDisplayData, selection.venueTypeName)
            : getVenuesForType(histVenueTypes, selection.venueTypeName);

        todayDisplayData = showTodayVenues
            ? filterByVenueType(todayDisplayData, selection.venueTypeName)
            : getVenuesForType(todayVenueTypes, selection.venueTypeName);
    }

    const onSelectionChanged = selection?.venueTypeName
        ? undefined
        : onChangeSelection;

    return (
        <div className="MarketVolBreakdown">
            <div
                className={classnames(
                    'grid-row',
                    'market',
                    'section-header',
                    !isVisible && 'section-hidden'
                )}
                onClick={onToggle}
            >
                <div className="labels">
                    <div className="header-title">Market</div>
                </div>
                <div className="content">
                    <ShowHideToggle status={isVisible ? 'hide' : 'show'} />
                </div>
            </div>
            {isVisible && (
                <div className="section-details ">
                    <div className="grid-row venue-bar-row historical">
                        <div className="labels">
                            <VenueAnalysisToggleLabel
                                name="Historical"
                                showVenues={showHistVenues}
                                disabled={histDisplayData.length < 1}
                                onClick={() =>
                                    toggleBreakdown('marketHistorical')
                                }
                                tooltip={historicalTooltip}
                            />
                        </div>
                        <div className="content">
                            {histDisplayData.map((venueType) => (
                                <VenueAnalysisBar
                                    width={width}
                                    data={venueType}
                                    showVenues={showHistVenues}
                                    key={venueType.name}
                                    highlightMode={
                                        showHistVenues ? 'VenueType' : 'Venue'
                                    }
                                    highlightedVenue={highlightedVenue}
                                    onHighlightedVenueChange={
                                        onHighlightedVenueChange
                                    }
                                    selection={selection}
                                    onSelectionChanged={onSelectionChanged}
                                />
                            ))}
                        </div>
                    </div>
                    <div className="grid-row venue-bar-row today">
                        <div className="labels">
                            <VenueAnalysisToggleLabel
                                name="Today"
                                showVenues={showTodayVenues}
                                disabled={todayDisplayData.length < 1}
                                onClick={() => toggleBreakdown('marketToday')}
                                tooltip={todayTooltip}
                            />
                        </div>
                        <div className="content today">
                            {todayDisplayData.map((venueType) => (
                                <VenueAnalysisBar
                                    data={venueType}
                                    showVenues={showTodayVenues}
                                    key={venueType.name}
                                    highlightMode={
                                        showTodayVenues ? 'VenueType' : 'Venue'
                                    }
                                    highlightedVenue={highlightedVenue}
                                    onHighlightedVenueChange={
                                        onHighlightedVenueChange
                                    }
                                    selection={selection}
                                    onSelectionChanged={onSelectionChanged}
                                    width={width}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
